// The hand: the card drawn this turn, from the moment it leaves the stage until
// the last pick of an aimed spell, so the player can keep reading it while they
// aim. The card of a spell with no target stays until the turn is over. In the
// compact layout it is the Spell tab of the tray. In the wide layout it rests
// on top of the log, and a click on a face of the wheel shows that face here.
// That face has a cross on its corner. The held card has none, because no click
// puts it away.
// The player can drag the card that rests on the log to any place in the window.

import { CARD_DRAG_MARGIN_PX } from '../constants';
import { heldCard } from '../game/flow';
import { el } from '../lib/dom';
import { makeDraggable } from '../lib/drag';
import { type CardOffset, cardRestStore, moveRestingCard } from '../state/cardRest';
import { gameStore } from '../state/game';
import { layoutStore } from '../state/layout';
import { stageStore } from '../state/stage';
import { closeWheelFace } from '../state/tray';
import { currentCue, subscribeView, viewedGame } from '../state/view';
import { wheelPickStore } from '../state/wheelPick';
import { createCloseButton } from './CloseButton';
import { createSpellCardFace } from './SpellCardFace';

const EMPTY_HAND_LINE = 'No card is held. The card from a cast stays here while you aim.';

export const createHand = (): HTMLElement => {
  const held = createSpellCardFace('card--held');
  const closeButton = createCloseButton(closeWheelFace);
  held.root.append(closeButton);
  // In the compact layout the hand is a tray panel, and an empty panel needs a reason.
  const empty = el('p', { className: 'hand__empty', textContent: EMPTY_HAND_LINE });
  const slot = el('div', { className: 'hand__slot' }, [held.root, empty]);

  const root = el('aside', { className: 'hand' }, [slot]);

  // The styles of the wide layout read the two values. The compact layout does not.
  const drawOffset = (offset: CardOffset): void => {
    root.style.setProperty('--card-drag-x', `${offset.x}px`);
    root.style.setProperty('--card-drag-y', `${offset.y}px`);
  };

  makeDraggable(held.root, {
    canDrag: () => layoutStore.get() === 'wide',
    offset: cardRestStore.get,
    onMove: moveRestingCard,
    margin: CARD_DRAG_MARGIN_PX,
    draggingClass: 'card--dragging',
  });
  cardRestStore.subscribe(drawOffset);

  const draw = (): void => {
    const state = viewedGame();
    const stage = stageStore.get();
    const cue = currentCue();
    const reviewing = cue.kind === 'review';

    // A face the player picked on the wheel shows in place of the held card.
    const pick = wheelPickStore.get();
    const pickedSpell = pick === null ? undefined : gameStore.get().wheel[pick];
    const showsPick = pick !== null && pickedSpell !== undefined;
    closeButton.hidden = !showsPick;
    if (pick !== null && pickedSpell !== undefined) {
      slot.classList.add('hand__slot--holding');
      held.root.hidden = false;
      held.draw({ spellId: pickedSpell, caster: null }, '');
      return;
    }

    // An earlier step shows the card that was held on its ply.
    const card = heldCard(state);
    const inTheAir = !reviewing && stage.phase !== 'hidden';
    const showing = card !== null && !inTheAir;
    slot.classList.toggle('hand__slot--holding', showing);
    held.root.hidden = !showing;
    if (!showing || !card) return;

    // The cue shows the prompt, so the card at rest has none.
    held.draw(card, '');
  };

  draw();
  subscribeView(draw);
  stageStore.subscribe(draw);
  wheelPickStore.subscribe(draw);
  gameStore.subscribe(draw);
  return root;
};
